/**
 * Public type surface for beckhoff-xts-viewer-3d.
 *
 * Anything that crosses the Component boundary — props, refs, callbacks —
 * is declared here.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Module + Mover + Tool taxonomy
// ─────────────────────────────────────────────────────────────────────────────

/** Beckhoff XTS module types supported by the 3D viewer. */
export type ModuleType3D =
  // Standard AT (Beckhoff)
  | 'AT2000_0250'
  | 'AT2001_0250'
  | 'AT2002_0250'
  | 'AT2002_0249'
  | 'AT2002_0249_ZX2002_0001'
  | 'AT2000_0233'
  | 'AT2000_0249'
  | 'AT2100_0250'
  | 'AT2102_0250'
  | 'AT2020_0250'
  | 'AT2021_0250'
  | 'AT2025_0250'
  | 'AT2026_0250'
  | 'AT2040_0250'
  | 'AT2041_0250'
  | 'AT2042_0250'
  | 'AT2140_0250'
  // 0500 + 0501 are merged into 0500_180 in normalizeXtsConfig.
  | 'AT2050_0500'
  | 'AT2050_0501'
  | 'AT2050_0500_180'
  // Eco AT2200
  | 'AT2200_0500'
  | 'AT2202_0500'
  // Hygienic ATH (path math identical to AT pendants)
  | 'ATH2000_0250'
  | 'ATH2001_0250'
  | 'ATH2002_0250'
  | 'ATH2020_0250'
  | 'ATH2040_0250'
  | 'ATH2041_0250'
  | 'ATH2042_0250'
  | 'ATH2050_0500'
  | 'ATH2050_0501'
  | 'ATH2050_0500_180';

/**
 * Rail system beneath the module. Only affects the visual mesh
 * (Beckhoff aluminium rail vs. Hepco GFX), not the path math.
 *
 * `Beckhoff` is the Beckhoff aluminium guiding rail (all
 * AT-Standard + Eco families); `HepcoGfx` is the alternative
 * Hepco GFX profile. ATH modules ship with their rail baked into
 * the module GLB and are independent of this choice.
 */
export type RailSystem = 'Beckhoff' | 'HepcoGfx';

/** Beckhoff XTS + Hepco mover types. `Custom` requires `customMoverLayout`. */
export type MoverType3D =
  | 'AT9011_0050'
  | 'AT9011_0070'
  | 'AT9012_0050'
  | 'AT9014_0055'
  | 'AT9014_0070'
  | 'ATH9011_0075'
  | 'ATH9013_0075'
  | 'Hepco_GFX2_1TC_S25'
  | 'Custom';

/** Predefined mover-tool / carrier-plate types. */
export type MoverToolType3D =
  | 'AT8200_1000_0100'
  | 'AT8200_2000_0100'
  | 'Custom';

/**
 * Beckhoff guiding-rail GLB variants. Rendered alongside the motor modules
 * when `RailSystem === 'Beckhoff'` to visualise the outer guide
 * profile that the mover wheels engage.
 *
 * Curve sign drives the selection (see `MODULE_GUIDING_RAIL_MAP`):
 *   - Straights: AT9000_0249 / AT9000_0250 / AT9000_0500
 *   - +22.5° curve: AT9020_1250
 *   - −22.5° curve: AT9025_1466
 *   - 45° curve:    AT9040_0750
 *   - 180° clothoid (entire AT2050 500 mm path): AT9050_0500
 *
 * Hygienic ATH modules ship with their guide profile baked into the module
 * GLB and therefore do NOT receive a separate guiding-rail mesh.
 */
export type RailType3D =
  | 'AT9000_0249'
  | 'AT9000_0250'
  | 'AT9000_0500'
  | 'AT9020_1250'
  | 'AT9025_1466'
  | 'AT9040_0750'
  | 'AT9050_0500';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration tree (XtsConfig)
// ─────────────────────────────────────────────────────────────────────────────

export type Vec3 = [number, number, number];
export type Vec2 = [number, number];

/** Pose of the entire XTS group in the world frame. */
export interface Orientation {
  positionMm?: Vec3;
  /** Intrinsic XYZ Euler angles in degrees. */
  rotationDegEuler?: Vec3;
}

/**
 * Per-part heatmap samples plus colour range. Rendered as a coloured
 * tube running along each part's centerline (with optional lateral /
 * vertical offset), with the tube's vertex colours interpolated between
 * `minColor` and `maxColor` according to the consumer-supplied
 * `(positionMm, value)` samples.
 *
 * Typical use: per-stator drive current, motor temperature, or fault
 * counters streamed from the controller and overlaid on the layout in
 * real time. Sample positions are interpreted in the XPU's user-frame
 * (`ProcessingUnitConfig.positionFrame`), same convention as mover /
 * station / area positions.
 */
export interface StatorHeatmap {
  /**
   * One bucket per `PartConfig.globalNumber`. Parts not listed render
   * no heatmap. Samples within a part are sorted by `positionMm`
   * before interpolation; values between consecutive samples are
   * linearly interpolated, values outside the sample range are
   * held flat at the nearest sample.
   */
  parts: Array<{
    partOid: number;
    samples: Array<{ positionMm: number; value: number }>;
  }>;
  /** Value mapped to `minColor`. Values ≤ min clamp to `minColor`. */
  min: number;
  /** Value mapped to `maxColor`. Values ≥ max clamp to `maxColor`. */
  max: number;
  /** Colour at `min`. Default `'#22c55e'` (green). */
  minColor?: string;
  /** Colour at `max`. Default `'#ef4444'` (red). */
  maxColor?: string;
  /** Tube thickness in mm. Default 6. */
  thicknessMm?: number;
  /** Vertical Z lift (mm). Default -15 — sits just below the rail. */
  displacementMm?: number;
  /** Lateral Y displacement (mm). Default 0. */
  lateralDisplacementMm?: number;
  /**
   * Path segments per module along the heatmap tube. More = smoother
   * gradient, more triangles. Default 8 (= 32 segments on a 4-module
   * straight, ~1 GPU vertex per 8 mm of track).
   */
  segmentsPerModule?: number;
  /** Tube opacity (0..1). Default 0.85. */
  opacity?: number;
}

export interface XtsConfig {
  orientation?: Orientation;
  processingUnits: ProcessingUnitConfig[];
  stations?: StationConfig[];
  /**
   * Labelled regions along the track. Like a StationConfig, but
   * without stop positions — only a coloured tube segment plus an
   * optional description billboard. For zone markings (e.g. "Manual
   * Access", "Safety Area", "Cleanroom") that have no
   * machine stops.
   */
  areas?: AreaConfig[];
  infoBars?: InfoBarConfig[];
  customAssets?: CustomAssetConfig[];
}

export interface ProcessingUnitConfig {
  /**
   * Stable host-supplied 32-bit unique identifier for this XPU. Required
   * at runtime — must be a non-negative integer that fits in a uint32
   * and is unique across all XPUs in the config.
   */
  objectId: number;
  moverType: MoverType3D;
  customMoverLayout?: CustomMoverLayout;
  /** Default: 'Beckhoff'. */
  railSystem?: RailSystem;
  parts: PartConfig[];
  movers: MoverConfig[];
  /**
   * Track-level pose, applied to the entire XPU subtree (modules, movers,
   * tools, stations, info bars, mover-bound custom assets). Composes
   * MULTIPLICATIVELY with the root `XtsConfig.orientation`:
   *
   *     world = orientation ⊗ trackTransform ⊗ partTransformation ⊗ chain
   *
   * `scaleUniform` is applied as a uniform scale factor (default 1). Non-
   * uniform scaling is intentionally not supported — it would distort path
   * radii and break GLB lighting.
   */
  trackTransform?: TrackTransform;
  /**
   * User-frame remap applied to every `partPositionMm`-style value inside
   * this XPU (mover positions, station start/end/stops, area start/end,
   * stop-position ghost movers). Lets a host that uses a different "zero"
   * or sign convention than the GLB chain enter positions in their own
   * frame; the renderer translates to the chain's intrinsic frame:
   *
   *     chainPos = originMm + (direction === 'negative' ? -userPos : userPos)
   *
   * Defaults: `direction: 'positive'`, `originMm: 0` — i.e. user-frame
   * equals chain-frame, no transform.
   */
  positionFrame?: PositionFrame;
}

/**
 * Sign + zero-offset convention for partPosition values within an XPU.
 * See `ProcessingUnitConfig.positionFrame` for semantics.
 */
export interface PositionFrame {
  /** Travel direction. Default: `'positive'`. */
  direction?: 'positive' | 'negative';
  /** Zero-point shift (in chain-coordinates mm). Default: 0. */
  originMm?: number;
}

export interface TrackTransform {
  positionMm?: Vec3;
  /** Intrinsic XYZ Euler angles in degrees. */
  rotationDegEuler?: Vec3;
  /** Uniform scale factor. Default 1. */
  scaleUniform?: number;
  /** Hide the entire XPU when false. Default true. */
  visible?: boolean;
}

export interface PartConfig {
  /**
   * Stable host-supplied 32-bit unique identifier for this part. Distinct
   * from `globalNumber` (server-side ObjectId): callers reference parts
   * by `objectId` at runtime — e.g. in the array form of
   * `XtsViewer3DRef.setMoverPositions` (`{partObjectId, partPosition}`).
   * Required at runtime — must be a non-negative integer that fits in a
   * uint32 and is unique across all parts in the system.
   * `normalizeXtsConfig` emits a `missing-part-object-id` /
   * `invalid-part-object-id` / `duplicate-part-object-id` warning when
   * the contract is violated.
   */
  objectId: number;
  /** = ServerTypes.PartModel.ObjectId — unique system-wide. */
  globalNumber: number;
  modules: ModuleEntry[];
  partTransformation?: PartTransformation;
}

export interface ModuleEntry {
  moduleType: ModuleType3D;
  /**
   * Stable host-supplied number for this module. Required at runtime —
   * simply counted up per module (1, 2, 3, …) across the whole system
   * and used as the index in `XtsViewer3DRef.setModuleStatuses(array)` —
   * i.e. `statuses[m.globalNumber] === { warning?: …, error?: … }`.
   * `normalizeXtsConfig` emits a `missing-module-global-number` /
   * `invalid-module-global-number` / `duplicate-module-global-number`
   * warning when the contract is violated. Typed optional only so
   * legacy configs and tests keep compiling; new code MUST set it.
   */
  globalNumber?: number;
  /**
   * Optional drive-status overlay for this module. Mirrors the
   * `MoverConfig.status` shape; the same per-mesh emissive blink +
   * billboard <DriveStatusIcon> surfaces both. Filtered by
   * `display.showDriveWarnings` / `display.showDriveErrors`.
   *
   * For live updates (e.g. drive warnings/errors streamed from PLC),
   * prefer the imperative
   * `XtsViewer3DRef.setModuleStatuses(array)` channel — index = this
   * module's `globalNumber`. The store wins over this config-time value.
   */
  status?: { warning?: boolean; error?: boolean };
}

/**
 * Per-part 3D pose, applied to EVERYTHING anchored to the part:
 * modules, guiding rails, movers, mover tools, mover-bound custom assets,
 * and the path-derived overlays (stations, areas, dimensions, info bars,
 * stop-position ghost movers, stator heatmap). Composes inside the
 * per-XPU `trackTransform`:
 *
 *     world = orientation ⊗ trackTransform ⊗ partTransformation ⊗ chain
 *
 * All fields are optional and default to identity (offset = [0,0,0],
 * rotation = [0,0,0]). Whole-track placement is done via
 * `ProcessingUnitConfig.trackTransform`; this transform is for moving a
 * part WITHIN its XPU — e.g. lifts that translate along Z, parts
 * mounted at an angle, or live-editable kinematics in the playground.
 *
 * Multi-part overlays (stations / areas spanning multiple `partOids`)
 * follow the transform of their FIRST partOid — the chain math
 * upstream is part-local, and points from differently-transformed
 * parts can't be stitched into a single tube. Anchor multi-part
 * overlays to the part you want them to track.
 */
export interface PartTransformation {
  /** Translation in mm, in the XPU's track-transform frame. */
  offsetMm?: Vec3;
  /** Intrinsic XYZ Euler angles in degrees. */
  rotationDegEuler?: Vec3;
  partSide?: 'Default' | 'Driver' | 'Encoder';
  /** Hide everything on this part when false. Default true. */
  visible?: boolean;
}

export interface MoverConfig {
  /** Position-on-track index within the XPU. */
  index: number;
  /**
   * Stable host-supplied 32-bit unique identifier for this mover.
   * Required, must be a non-negative integer that fits in a uint32 and
   * is unique across all movers in the config.
   */
  id: number;
  /** References PartConfig.globalNumber. */
  partOid: number;
  /** [0, part.trackLengthMm] — clamped when out-of-bounds. */
  partPositionMm: number;
  status?: { warning?: boolean; error?: boolean };
  selected?: boolean;
  /** Predefined tool carriers or custom GLBs attached to this mover. */
  tools?: MoverToolConfig[];
}

/**
 * Runtime mover-position update entry — pushed through
 * `XtsViewer3DRef.setMoverPositions(array)`. The array is indexed by
 * `MoverConfig.index` (i.e. entry `i` targets the mover with
 * `index === i`); use `null` / `undefined` to skip a slot. The
 * `partObjectId` field references `PartConfig.objectId`.
 */
export interface MoverPositionEntry {
  /** = PartConfig.objectId (the host-side 32-bit unique part ID). */
  partObjectId: number;
  /** Position along the referenced part, in user-frame mm. */
  partPosition: number;
}

/**
 * Runtime drive-status entry for a single module — pushed through
 * `XtsViewer3DRef.setModuleStatuses(array)`. The array is indexed by
 * `ModuleEntry.globalNumber`; use `null` / `undefined` to clear a slot.
 */
export interface ModuleStatusEntry {
  warning?: boolean;
  error?: boolean;
}

export interface MoverToolConfig {
  toolType: MoverToolType3D;
  /** Required when toolType === 'Custom'. */
  customGlbUrl?: string;
  customOriginCorrection?: {
    translateMm: Vec3;
    rotationDegEuler: Vec3;
  };
  /** Local placement on the mover, relative to the magnet-plate center. */
  offsetMm?: Vec3;
  rotationDegEuler?: Vec3;
  opacity?: number;
  visible?: boolean;
  id?: string;
}

export interface CustomMoverLayout {
  glbUrl: string;
  originCorrection?: {
    translateMm: Vec3;
    rotationDegEuler: Vec3;
  };
  magnetPlateCenterMm?: Vec3;
  pathLengthMm?: number;
  imageFrontUrl?: string;
  imageBackUrl?: string;
}

export type CustomAssetBinding =
  | {
      type: 'static';
      positionMm: Vec3;
      rotationDegEuler: Vec3;
      scale?: number;
    }
  | {
      type: 'mover';
      moverRef: { processingUnitObjectId: number; moverIndex: number };
      offsetMm?: Vec3;
      rotationDegEuler?: Vec3;
      /** Uniform scale (default 1). */
      scale?: number;
    }
  | {
      type: 'all-movers';
      offsetMm?: Vec3;
      rotationDegEuler?: Vec3;
      /** Uniform scale (default 1). Applied per mover. */
      scale?: number;
    };

export interface CustomAssetConfig {
  id: string;
  glbUrl: string;
  binding: CustomAssetBinding;
  opacity?: number;
  visible?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Display options
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Y-axis / rotation convention for incoming position data.
 *
 *   - `'viewer'` (default): values are taken as-is in the viewer's
 *     right-handed Z-up frame (+Y = left when looking along +X).
 *   - `'beckhoff-xts'`: the Beckhoff XTS `OriginTransform` convention.
 *     The viewer negates every Y translation (`offsetMm[1]`,
 *     `trackTransform.positionMm[1]`) and every Z-Euler rotation
 *     (`rotationDegEuler[2]`) so that data exported from XAE
 *     Konfigurator / `.xti` files renders with the correct topology
 *     without any consumer-side sign flip.
 */
export type CoordinateSystem = 'viewer' | 'beckhoff-xts';

export interface DisplayOptions {
  /**
   * Y-axis convention for all spatial inputs (`PartTransformation`,
   * `TrackTransform`). Set to `'beckhoff-xts'` when binding directly
   * to XAE / XTI / `<XtsConstruction>` data so the viewer negates Y
   * translations and Z-Euler rotations internally. Default:
   * `'viewer'` (no transform — current behaviour).
   */
  coordinateSystem?: CoordinateSystem;
  displayStations?: boolean;
  /** Render configured areas. Default: true (when `areas[]` is set). */
  displayAreas?: boolean;
  displayDimensions?: boolean;
  showDriveErrors?: boolean;
  showDriveWarnings?: boolean;
  /**
   * Render each mover's `id` as a billboarded 3D text label above the
   * mover. Toggle on/off without re-creating the scene. Visual options
   * (font size, color, offset, font URL, outline) are configurable via
   * `moverIdLabelOptions`. Default: false.
   */
  showMoverIds?: boolean;
  /**
   * Style + placement overrides for the mover-id labels. Only applied
   * when `showMoverIds === true`. All fields optional; sensible defaults
   * shipped by the renderer.
   */
  moverIdLabelOptions?: MoverIdLabelOptions;
  invertDimensions?: boolean;
  stationOptions?: StationOptions;
  stationMarkerOptions?: MarkerOptions;
  /**
   * Render a static, semi-transparent ghost mover at every `stopPositions`
   * of every enabled `StationConfig`. Useful for layout reviews — shows
   * "where will the mover be when stopped" without driving any positions.
   * Ghosts are non-interactive, never participate in selection / collision
   * detection, and use the XPU's `moverType` for their GLB.
   *
   * Default: false.
   */
  showStopPositionMovers?: boolean;
  /** Visual options for the stop-position ghost movers. */
  stopPositionMoverOptions?: StopPositionMoverOptions;
  /**
   * Render the stator heatmap overlay. Requires the `statorHeatmap`
   * prop on `<XtsViewer3D>` to be set; without data the toggle is a
   * no-op. Default: false.
   */
  showStatorHeatmap?: boolean;
  areaOptions?: AreaOptions;
  dimensionOptions?: DimensionOptions;
  infoBarOptions?: InfoBarOptions;
  /**
   * Render small red spheres at the 8 AABB corners of every module GLB
   * (the GLB's CAD-frame AABB, transformed through the origin-correction).
   *
   * Use during calibration to verify that adjacent modules' mating-faces
   * are in geometrically the same place — when the corner spheres of two
   * neighbouring modules overlap, the chain has C0 continuity at that
   * boundary. Off by default.
   */
  showModuleCornerMarkers?: boolean;
  /** Diameter (mm) of the corner-marker spheres. Default: 10. */
  moduleCornerMarkerSizeMm?: number;
  /** Color of the corner markers. Default: '#FF2030' (red). */
  moduleCornerMarkerColor?: string;
  /**
   * Render shadows: enables the WebGL shadow map, makes one directional
   * light cast shadows, and renders a transparent shadow-catcher plane
   * so shadows from modules / movers / tools become visible. Default:
   * false (no shadow-map cost when disabled). All loaded GLB meshes
   * have their `castShadow` + `receiveShadow` flags set unconditionally
   * — turning shadows on at the canvas level is the only thing that
   * actually triggers the per-frame shadow pass.
   */
  shadows?: boolean;
  /**
   * Image-based lighting via a procedural indoor environment map
   * (three's `RoomEnvironment` processed through `PMREMGenerator` —
   * no HTTP fetch, no asset shipped). Drives PBR reflections on
   * metallic / roughness materials so aluminium rails and brushed
   * mover plates read as actual metal instead of flat shaded surfaces.
   *
   * Cost: one-time PMREM build on mount (≈ 1.5 MB GPU), zero per-frame
   * overhead beyond the regular PBR shader's environment lookup.
   *
   * Default: `true` — looks noticeably better at no measurable cost.
   * Set `false` to fall back to direct-lighting only.
   */
  environmentLighting?: boolean;
  /**
   * Strength of the environment-map contribution. Lower values keep
   * the look closer to the previous direct-lighting-only baseline;
   * higher values emphasise reflections.
   *
   * Default: `0.4` — reflections visible without overwhelming the
   * directional + ambient lights. Range typically `0.0` … `1.5`.
   */
  environmentIntensity?: number;
  /**
   * Renderer tone-mapping operator. ACES filmic compresses bright
   * highlights into a perceptually pleasant rolloff and is the closest
   * match to film / professional CAD viewers; `'linear'` reproduces the
   * pre-tone-mapping behaviour for callers who relied on it.
   *
   * Default: `'aces'`.
   */
  toneMapping?: 'aces' | 'linear' | 'reinhard' | 'cineon' | 'agx' | 'none';
  /**
   * Tone-mapping exposure multiplier (analogous to a camera's exposure
   * stop). 1.0 = neutral. Bumped above 1 brightens the result, below 1
   * darkens it.
   *
   * Default: `1.0`.
   */
  toneMappingExposure?: number;
  /**
   * Screen-space ambient occlusion (GTAO). Adds contact shadows in
   * crevices and between adjacent surfaces for significantly more visual
   * depth. Cost: one half-res pass per frame.
   *
   * Default: `false`.
   */
  ssao?: boolean;
  /**
   * SSAO blend intensity. Higher values darken crevices more.
   * Range: 0.0–4.0. Default: `1.0`.
   */
  ssaoIntensity?: number;
  /**
   * SSAO sampling radius in world units (mm). Larger values spread the
   * occlusion over a wider area. Default: `20`.
   */
  ssaoRadius?: number;
  /**
   * Bloom (glow) for bright metallic surfaces. Adds a soft halo around
   * polished rails and highlights, giving metals a more realistic
   * reflective appearance. Cost: one half-res pass per frame.
   *
   * Default: `false`.
   */
  bloom?: boolean;
  /**
   * Bloom intensity (strength). Higher values produce brighter glow.
   * Range: 0.0–3.0. Default: `0.4`.
   */
  bloomIntensity?: number;
  /**
   * Bloom luminance threshold. Only pixels brighter than this value
   * contribute to the bloom. Lower values bloom more of the scene.
   * Range: 0.0–1.0. Default: `0.85`.
   */
  bloomThreshold?: number;
  /**
   * Bloom blur radius. Controls how far the glow spreads.
   * Range: 0.0–1.0. Default: `0.4`.
   */
  bloomRadius?: number;
}

/**
 * Style + placement overrides for the per-mover-id 3D text labels
 * surfaced when `DisplayOptions.showMoverIds === true`.
 *
 * Defaults (all in scene units / mm; chosen for legibility on the
 * default mover GLB at typical XTS layouts):
 *   - `fontSizeMm`: 15
 *   - `color`: '#000000'
 *   - `offsetMm`: [0, 0, 55]  // centred above the mover's magnet plate
 *   - `fontUrl`: `DEFAULT_LABEL_FONT_URL` (Roboto Condensed)
 *   - `outlineWidthMm`: 0    // no outline
 *   - `outlineColor`: '#FFFFFF'
 */
export interface MoverIdLabelOptions {
  /** Text height in mm (world units). Default: 15. */
  fontSizeMm?: number;
  /** CSS color for the text fill. Default: '#000000'. */
  color?: string;
  /**
   * Offset of the label's anchor point relative to the mover's local
   * origin (= magnet-plate center), in mm.
   * `[x, y, z]` — Z is up. Default: `[0, 0, 25]`.
   */
  offsetMm?: Vec3;
  /**
   * URL of a TTF / OTF / WOFF font, passed straight to troika-three-text.
   * Default: `DEFAULT_LABEL_FONT_URL` (Roboto Condensed via jsDelivr).
   */
  fontUrl?: string;
  /** Outline thickness in mm. 0 disables the outline. Default: 0. */
  outlineWidthMm?: number;
  /** Outline color. Only applied if `outlineWidthMm > 0`. Default: '#FFFFFF'. */
  outlineColor?: string;
}

export interface StationOptions {
  thicknessMm?: number;
  /** Vertical Z lift (mm). Positive = above the table. */
  displacementMm?: number;
  /**
   * Lateral Y displacement (mm) along the path's left-hand normal in the
   * XY plane. Mirrors the Dimensions overlay: each tick at centerline s mm
   * is rendered perpendicular to that point — values stay 1:1 with the
   * actual track.
   */
  lateralDisplacementMm?: number;
  /** Text styling for station description + stop-position labels. */
  textOptions?: TextOptions;
  /**
   * Render the StationConfig.description as a camera-facing billboard at
   * the centre of the station tube. Default: false.
   */
  showStationDescription?: boolean;
  /**
   * Render each StopPosition's mm value as a billboard next to its stop
   * marker. Default: false.
   */
  showStopPositionValues?: boolean;
  /**
   * Override the per-label color. Falls back to the per-station
   * `StationConfig.stationColor` (so colourful tubes keep matching their
   * labels by default). Set this when you want a single uniform label
   * colour across all stations regardless of tube colour.
   */
  labelColorOverride?: string;
}

/**
 * Visual options for the stop-position ghost movers (rendered when
 * `display.showStopPositionMovers === true`).
 */
export interface StopPositionMoverOptions {
  /**
   * Material opacity applied to the cloned mover GLB. Default 0.45 —
   * ghost-like without disappearing. Set 1 for fully opaque, 0 to hide.
   */
  opacity?: number;
  /**
   * When set, applies an emissive tint matching the station colour
   * (`StationConfig.stationColor`). Default: `true` — ghosts adopt the
   * tube colour so different zones read as distinct. Set `false` for
   * a uniform pale ghost regardless of station colour.
   */
  tintByStation?: boolean;
}

/**
 * Display options for `AreaConfig[]`. Mirror of `StationOptions` minus the
 * stop-position-related fields — Areas are pure range-overlays (tube +
 * optional description billboard).
 */
export interface AreaOptions {
  /** Tube thickness in mm. Default: 8. */
  thicknessMm?: number;
  /** Vertical Z lift (mm). Positive = above the table. Default: 60. */
  displacementMm?: number;
  /**
   * Lateral Y displacement (mm) along the path's left-hand normal in the
   * XY plane. Each sample at centerline s is rendered perpendicular to that
   * point — values stay 1:1 with the actual track.
   */
  lateralDisplacementMm?: number;
  /** Text styling for area description labels. */
  textOptions?: TextOptions;
  /**
   * Render the AreaConfig.description as a camera-facing billboard at the
   * centre of the area tube. Default: true.
   */
  showAreaDescription?: boolean;
  /**
   * Override the per-label color. Falls back to the per-area
   * `AreaConfig.color`. Set this when you want a single uniform label colour
   * across all areas regardless of tube colour.
   */
  labelColorOverride?: string;
  /**
   * Tube opacity (0..1). Areas often cover long stretches of the track —
   * lowering opacity (e.g. 0.5) makes them feel like a translucent zone
   * marker rather than a solid pipe. Default: 1.
   */
  opacity?: number;
}

/**
 * Marker shape palette.
 *
 *   - Diamond  : OctahedronGeometry, oriented with +X along path (existing)
 *   - Tick     : narrow BoxGeometry across the path (existing)
 *   - Sphere   : isotropic SphereGeometry (no orientation)
 *   - Cone     : ConeGeometry with apex up (+Z), base on path point
 *   - Cube     : cube oriented along the path tangent (chunkier than Tick)
 *   - Cylinder : pillar standing perpendicular to the path (axis = +Z)
 *   - None     : not rendered
 */
export type MarkerShape =
  | 'Diamond'
  | 'Tick'
  | 'Sphere'
  | 'Cone'
  | 'Cube'
  | 'Cylinder'
  | 'None';

export interface MarkerOptions {
  shape?: MarkerShape;
  sizeMm?: number;
}

export interface DimensionOptions {
  thicknessMm?: number;
  /**
   * Vertical displacement (mm), added to the path centerline's Z when
   * sampling the dimension overlay. Positive = above the table.
   */
  displacementMm?: number;
  /**
   * Lateral displacement (mm) along the path's left-hand normal in the XY
   * plane (+Y in path-frame). Lets you offset the dimension tube + ticks
   * sideways from the mover centerline. On curves the offset tube has a
   * different arc length than the centerline, but the displayed values
   * still match the centerline position — each tick at centerline s mm
   * is rendered perpendicular to that point.
   */
  lateralDisplacementMm?: number;
  textOptions?: TextOptions;
  textPattern?: number;
  /**
   * Show numeric mm-value labels at every dimension tick (module boundaries
   * and — when enabled — intermediate steps). Labels are billboards that
   * always face the camera. Default: false.
   */
  showValues?: boolean;
  /**
   * Place additional dimension ticks every N millimetres along the path,
   * on top of the per-module-boundary ticks. 0 disables intermediate
   * ticks (default). Typical values: 50, 100, 250.
   */
  intermediateStepMm?: number;
  /**
   * Marker style + size for intermediate ticks. Defaults to a smaller
   * `Tick` marker (60% of the boundary tick size) so the intermediates
   * remain visually subordinate.
   */
  intermediateMarkerOptions?: MarkerOptions;
  /**
   * Whether intermediate ticks also get value labels. Only applies when
   * `showValues` is true. Default: true.
   */
  showIntermediateValues?: boolean;
  /**
   * What number to display next to each tick:
   *   - 'partPosition' (default): cumulative mm from the part's start.
   *   - 'fromModule'  : mm from the start of the containing module.
   *   - 'both'        : "<part> mm (<module> mm)".
   */
  valueMode?: 'partPosition' | 'fromModule' | 'both';
}

export interface InfoBarOptions {
  defaultThicknessMm?: number;
  textOptions?: TextOptions;
}

export interface TextOptions {
  sizeMm?: number;
  color?: string;
  fontFamily?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stations / Dimensions / InfoBars
// ─────────────────────────────────────────────────────────────────────────────

export interface StationConfig {
  stationId: number;
  description: string;
  isEnabled: boolean;
  partOids: number[];
  startPositionOnPart: number;
  endPositionOnPart: number;
  /** ARGB int (same as 2D). */
  stationColor: number;
  stopPositions?: number[];
  /**
   * Reference frame for `stopPositions` values:
   *   - `'track'`   (default) — values are absolute partPositionMm from
   *                              track start. Same convention as
   *                              `startPositionOnPart` / `endPositionOnPart`.
   *   - `'station'` — values are RELATIVE to `startPositionOnPart`. So
   *                   `0` puts a stop right at the station's start;
   *                   `100` puts one 100 mm into the station regardless
   *                   of where the station lives on the part.
   *
   * Useful when stops are conceptually tied to the station's geometry
   * (entry, mid, exit) rather than absolute mm marks on the track.
   */
  stopPositionsRelativeTo?: 'track' | 'station';
  /**
   * Per-station override of the stop-marker geometry. Falls back to
   * `display.stationMarkerOptions.shape` when omitted. Lets one station
   * render Diamonds while a neighbour uses Cones, etc.
   */
  stopMarkerShape?: MarkerShape;
  /** Per-station override of the stop-marker size (mm). */
  stopMarkerSizeMm?: number;
}

/**
 * Labelled region along the track. Identical to
 * `StationConfig`, but without stop positions — areas only carry text +
 * colour and describe a zone (e.g. "Cleanroom", "Manual Access",
 * "Safety Loop") that does not trigger machine stops.
 *
 * Multi-part: same semantics as `StationConfig.partOids`. With a single
 * partOid and `endPositionOnPart < startPositionOnPart`, a closed loop
 * is rendered (to the end of the track, then from 0 to End).
 */
export interface AreaConfig {
  areaId: number;
  description: string;
  isEnabled: boolean;
  partOids: number[];
  startPositionOnPart: number;
  endPositionOnPart: number;
  /** ARGB int (same as StationConfig). Used for tube + default label. */
  color: number;
}

export interface InfoBarConfig {
  processingUnitObjectId: number;
  partObjectId: number;
  partStartPositionMm: number;
  partEndPositionMm: number;
  thickness: number;
  displacement: number;
  color: { color: string };
  visible: boolean;
  text?: string;
  textPlacement?: 'Left' | 'Center' | 'Right';
  textDisplacement?: number;
  textOptions?: TextOptions;
  markers?: InfoBarMarkerConfig[];
  zIndex: number;
}

export interface InfoBarMarkerConfig {
  positionMm: number;
  shape: 'Diamond' | 'Tick' | 'None';
  sizeMm: number;
  color?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Selection + Camera
// ─────────────────────────────────────────────────────────────────────────────

export interface ModuleRef {
  processingUnitObjectId: number;
  partObjectId: number;
  moduleIndex: number;
}

export interface MoverRef {
  processingUnitObjectId: number;
  moverIndex: number;
}

export interface SelectionState {
  modules: ModuleRef[];
  movers: MoverRef[];
}

export type SelectionMode = 'Off' | 'Single' | 'Multi';

/**
 * Declarative module highlight. All modules whose `moduleType` appears in
 * `moduleTypes` are tinted with the given `color` — same emissive effect as
 * selection, but independent of the selection state. Multiple highlights can
 * overlap; the first matching entry wins.
 *
 * Toggle on/off by setting `moduleHighlights` on `<XtsViewer3D>` to an
 * array (enabled) or `undefined` / `[]` (disabled).
 */
export interface ModuleHighlight {
  /** Module types to highlight. */
  moduleTypes: ModuleType3D[];
  /** Highlight color (CSS color string, e.g. `'#FF6600'`). */
  color: string;
}

export interface CameraState {
  positionMm: Vec3;
  targetMm: Vec3;
  /** Always 'Z' in the current spec. */
  upAxis: 'Z' | 'Y';
  zoom?: number;
}

/**
 * Live camera projection.
 *   - `'perspective'` (default) — the regular 3D view.
 *   - `'orthographic'` — paired with `topDown`, gives the flat 2D plan view
 *     that matches `exportScreenshot({ mode: 'top-down' })`.
 */
export type CameraProjection = 'perspective' | 'orthographic';

/**
 * Target for the imperative `viewerRef.current.focusOn(target, opts)` camera
 * animation. The viewer computes the target's world-space bounding box and
 * flies the camera so the whole object fits the frame:
 *   - In `'perspective'` projection the current view angle is preserved
 *     (the camera only dollies / re-centres).
 *   - In `'orthographic'` top-down the frustum is re-framed and the camera
 *     pans straight over the object.
 */
export type FocusTarget =
  | { kind: 'scene' }
  | { kind: 'station'; stationId: number }
  | { kind: 'area'; areaId: number }
  | { kind: 'mover'; ref: MoverRef }
  | { kind: 'module'; ref: ModuleRef };

export interface FocusOptions {
  /** Animation duration in ms. Default 700. `0` jumps immediately. */
  durationMs?: number;
  /**
   * Padding around the target's bounding box (1 = tight fit). Default 1.2 in
   * perspective, 1.1 in orthographic top-down.
   */
  paddingFactor?: number;
  /** Easing curve. Default `'easeInOutCubic'`. */
  easing?: 'linear' | 'easeInOutCubic';
}

// ─────────────────────────────────────────────────────────────────────────────
// Errors + asset manifest
// ─────────────────────────────────────────────────────────────────────────────

export type XtsViewerErrorCode =
  | 'asset-load-failed'
  | 'unknown-module-type'
  | 'unknown-mover-type'
  | 'unknown-tool-type'
  | 'invalid-config'
  | 'unmatched-clothoid-half'
  | 'webgl-context-lost';

export interface XtsViewerError {
  code: XtsViewerErrorCode;
  message: string;
  details?: unknown;
}

export class XtsViewerErrorException extends Error {
  public readonly code: XtsViewerErrorCode;
  public readonly details?: unknown;
  constructor(code: XtsViewerErrorCode, message: string, details?: unknown) {
    super(message);
    this.name = 'XtsViewerError';
    this.code = code;
    this.details = details;
  }
}

export interface AssetManifest {
  /** Override per moduleType. Falls back to default mapping otherwise. */
  modules?: Partial<Record<ModuleType3D, ModuleAssetEntry>>;
  movers?: Partial<Record<MoverType3D, MoverAssetEntry>>;
  tools?: Partial<Record<MoverToolType3D, MoverToolAssetEntry>>;
  guidingRails?: Partial<Record<RailType3D, RailAssetEntry>>;
}

export interface RailAssetEntry {
  glbUrl: string;
  sidecarUrl?: string;
}

export interface ModuleAssetEntry {
  /** Per-RailSystem GLB URL (relative to assetsBaseUrl). */
  glbByRailSystem: {
    Beckhoff: string | null;
    HepcoGfx?: string | null;
  };
  sidecarUrl?: string;
}

export interface MoverAssetEntry {
  glbUrl: string;
  sidecarUrl?: string;
}

export interface MoverToolAssetEntry {
  glbUrl: string;
  sidecarUrl?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// XtsModelDocument (canonical export format)
// ─────────────────────────────────────────────────────────────────────────────

export interface XtsModelDocument {
  schemaVersion: 1;
  meta: {
    generatedAt: string;
    generator: string;
    sourceFormat?: string;
    sourceFileHash?: string;
  };
  config: XtsConfig;
  runtime?: XtsRuntimeState;
  railSystem?: RailSystem | { byProcessingUnit: Record<number, RailSystem> };
}

export interface XtsRuntimeState {
  movers: Array<{
    processingUnitObjectId: number;
    moverIndex: number;
    id: number;
    partOid: number;
    partPositionMm: number;
    velocity?: { mmPerSec: number };
    status?: { warning?: boolean; error?: boolean };
  }>;
  selection?: SelectionState;
  camera?: CameraState;
  display?: Partial<DisplayOptions>;
}
